import { useState } from 'react'
import { Card, Button, Table, Badge, Modal, Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { useAuth } from '../../hooks/useAuth'
import api from '../../services/api'

const BankFormats = () => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [selectedFormat, setSelectedFormat] = useState(null)
  const [formData, setFormData] = useState({
    nombre: '',
    formato: 'CSV',
    configuracion: {},
    activo: true
  })
  const [errors, setErrors] = useState({})
  const [showInfoModal, setShowInfoModal] = useState(false)

  // Obtener formatos bancarios
  const { data: formatos, isLoading } = useQuery(
    'bank-formats',
    async () => {
      const response = await api.get('/financial/formatos-bancarios/')
      return response.data.results || response.data || []
    },
    { enabled: !!user }
  )

  // Mutación para crear/actualizar formato
  const saveMutation = useMutation(
    async (data) => {
      if (selectedFormat) {
        const response = await api.put(`/financial/formatos-bancarios/${selectedFormat.id}/`, data)
        return response.data
      } else {
        const response = await api.post('/financial/formatos-bancarios/', data)
        return response.data
      }
    },
    {
      onSuccess: () => {
        toast.success(selectedFormat ? 'Formato actualizado exitosamente' : 'Formato creado exitosamente')
        queryClient.invalidateQueries('bank-formats')
        setShowForm(false)
        setSelectedFormat(null)
        resetForm()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || 'Error al guardar formato'
        toast.error(errorMsg)
        if (error.response?.data) {
          setErrors(error.response.data)
        }
      }
    }
  )

  // Mutación para eliminar formato
  const deleteMutation = useMutation(
    async (id) => {
      const response = await api.delete(`/financial/formatos-bancarios/${id}/`)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Formato eliminado exitosamente')
        queryClient.invalidateQueries('bank-formats')
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al eliminar formato'
        toast.error(errorMsg)
      }
    }
  )

  const resetForm = () => {
    setFormData({
      nombre: '',
      formato: 'CSV',
      configuracion: {},
      activo: true
    })
    setErrors({})
  }

  const handleCreate = () => {
    setSelectedFormat(null)
    resetForm()
    setShowForm(true)
  }

  const handleEdit = (formato) => {
    setSelectedFormat(formato)
    setFormData({
      nombre: formato.nombre || '',
      formato: formato.formato || 'CSV',
      configuracion: formato.configuracion || {},
      activo: formato.activo !== undefined ? formato.activo : true
    })
    setErrors({})
    setShowForm(true)
  }

  const handleDelete = (formato) => {
    if (window.confirm(`¿Está seguro de que desea eliminar el formato "${formato.nombre}"?`)) {
      deleteMutation.mutate(formato.id)
    }
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validateJSON = (jsonString) => {
    if (!jsonString || jsonString.trim() === '') {
      return { valid: true, config: {}, error: null }
    }
    try {
      const config = JSON.parse(jsonString)
      if (typeof config !== 'object' || Array.isArray(config)) {
        return { valid: false, config: null, error: 'La configuración debe ser un objeto JSON válido' }
      }
      return { valid: true, config, error: null }
    } catch (e) {
      return { valid: false, config: null, error: `JSON inválido: ${e.message}` }
    }
  }

  const validateConfigStructure = (config, formato) => {
    if (!config || Object.keys(config).length === 0) {
      return { valid: true, error: null }
    }

    if (formato === 'CSV') {
      // Validar estructura para CSV
      if (config.column_mapping && typeof config.column_mapping !== 'object') {
        return { valid: false, error: 'column_mapping debe ser un objeto' }
      }
      if (config.delimiter && typeof config.delimiter !== 'string') {
        return { valid: false, error: 'delimiter debe ser una cadena de texto' }
      }
      if (config.encoding && typeof config.encoding !== 'string') {
        return { valid: false, error: 'encoding debe ser una cadena de texto' }
      }
    } else if (formato === 'XML') {
      // Validar estructura para XML
      if (config.xpath_mapping && typeof config.xpath_mapping !== 'object') {
        return { valid: false, error: 'xpath_mapping debe ser un objeto' }
      }
    } else if (formato === 'OFX') {
      // OFX generalmente no requiere configuración personalizada
      // pero puede tener opciones adicionales
    } else if (formato === 'personalizado') {
      // Para personalizado, cualquier estructura es válida
    }

    return { valid: true, error: null }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    
    const newErrors = {}
    
    // Validar nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido'
    } else if (formData.nombre.trim().length < 3) {
      newErrors.nombre = 'El nombre debe tener al menos 3 caracteres'
    } else if (formData.nombre.trim().length > 100) {
      newErrors.nombre = 'El nombre no puede exceder 100 caracteres'
    }
    
    // Validar formato
    if (!formData.formato) {
      newErrors.formato = 'El formato es requerido'
    }
    
    // Validar configuración JSON
    const configTextarea = document.querySelector('textarea[name="configuracion"]')
    if (configTextarea) {
      const jsonValidation = validateJSON(configTextarea.value)
      if (!jsonValidation.valid) {
        newErrors.configuracion = jsonValidation.error
      } else {
        // Validar estructura según el formato
        const structureValidation = validateConfigStructure(jsonValidation.config, formData.formato)
        if (!structureValidation.valid) {
          newErrors.configuracion = structureValidation.error
        } else {
          // Actualizar formData con la configuración validada
          setFormData(prev => ({
            ...prev,
            configuracion: jsonValidation.config
          }))
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    saveMutation.mutate(formData)
  }

  const getFormatoBadge = (formato) => {
    const badges = {
      'CSV': { bg: 'primary', text: 'CSV' },
      'OFX': { bg: 'info', text: 'OFX' },
      'XML': { bg: 'warning', text: 'XML' },
      'personalizado': { bg: 'secondary', text: 'Personalizado' }
    }
    const badge = badges[formato] || { bg: 'secondary', text: formato }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getConfigPreview = (config) => {
    if (!config || Object.keys(config).length === 0) {
      return <span className="text-muted">Sin configuración</span>
    }
    try {
      return (
        <pre className="mb-0" style={{ fontSize: '0.85rem', maxHeight: '100px', overflow: 'auto' }}>
          {JSON.stringify(config, null, 2)}
        </pre>
      )
    } catch (e) {
      return <span className="text-muted">Configuración inválida</span>
    }
  }

  const getConfigExamples = () => {
    return {
      CSV: {
        title: 'Ejemplo de Configuración CSV',
        description: 'Configuración para archivos CSV con mapeo de columnas por índice',
        example: {
          delimiter: ',',
          encoding: 'utf-8',
          column_mapping: {
            fecha: 0,
            descripcion: 1,
            monto: 2,
            referencia: 3
          }
        },
        explanation: 'Los valores en column_mapping representan el índice de la columna (empezando desde 0). Por ejemplo, fecha: 0 significa que la fecha está en la primera columna.'
      },
      OFX: {
        title: 'Ejemplo de Configuración OFX',
        description: 'OFX generalmente no requiere configuración personalizada, pero puedes agregar opciones adicionales',
        example: {
          timezone: 'America/Mexico_City',
          currency: 'MXN'
        },
        explanation: 'OFX es un formato estándar que se procesa automáticamente. La configuración es opcional y puede incluir opciones como timezone o currency.'
      },
      XML: {
        title: 'Ejemplo de Configuración XML',
        description: 'Configuración para archivos XML con mapeo de rutas XPath',
        example: {
          xpath_mapping: {
            fecha: './/fecha',
            descripcion: './/descripcion',
            monto: './/monto',
            referencia: './/referencia'
          }
        },
        explanation: 'Los valores en xpath_mapping son rutas XPath que apuntan a los elementos en el XML. Usa .// para buscar en cualquier nivel del documento.'
      },
      personalizado: {
        title: 'Ejemplo de Configuración Personalizada',
        description: 'Configuración libre para formatos personalizados',
        example: {
          tipo_archivo: 'txt',
          separador: '|',
          campos: ['fecha', 'descripcion', 'monto'],
          formato_fecha: 'DD/MM/YYYY'
        },
        explanation: 'Para formatos personalizados, puedes definir cualquier estructura JSON según tus necesidades específicas.'
      }
    }
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Formatos Bancarios</h3>
        <Button variant="primary" onClick={handleCreate}>
          <i className="bi bi-plus-circle me-1"></i>
          Nuevo Formato
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Cargando...</span>
          </Spinner>
        </div>
      ) : (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Formatos Configurados</h5>
          </Card.Header>
          <Card.Body>
            {formatos && formatos.length > 0 ? (
              <Table responsive striped hover>
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Formato</th>
                    <th>Configuración</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {formatos.map((formato) => (
                    <tr key={formato.id}>
                      <td>{formato.nombre}</td>
                      <td>{getFormatoBadge(formato.formato)}</td>
                      <td>
                        <div style={{ maxWidth: '300px', maxHeight: '100px', overflow: 'auto' }}>
                          {getConfigPreview(formato.configuracion)}
                        </div>
                      </td>
                      <td>
                        <Badge bg={formato.activo ? 'success' : 'secondary'}>
                          {formato.activo ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(formato)}
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-pencil me-1"></i>
                            <span>Editar</span>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(formato)}
                            disabled={deleteMutation.isLoading}
                            style={{ minWidth: '80px' }}
                          >
                            <i className="bi bi-trash me-1"></i>
                            <span>Eliminar</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info">
                No hay formatos bancarios configurados. Crea uno nuevo para comenzar.
              </Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Modal de formulario */}
      <Modal show={showForm} onHide={() => { setShowForm(false); resetForm(); setSelectedFormat(null) }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedFormat ? 'Editar Formato Bancario' : 'Nuevo Formato Bancario'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    isInvalid={!!errors.nombre}
                    placeholder="Ej: BBVA CSV, Santander OFX"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.nombre}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Formato *</Form.Label>
                  <Form.Select
                    name="formato"
                    value={formData.formato}
                    onChange={handleChange}
                    isInvalid={!!errors.formato}
                  >
                    <option value="CSV">CSV</option>
                    <option value="OFX">OFX</option>
                    <option value="XML">XML</option>
                    <option value="personalizado">Personalizado</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.formato}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="d-flex align-items-center gap-2">
                Configuración (JSON) {formData.formato === 'CSV' && '*'}
                <Button
                  variant="outline-info"
                  size="sm"
                  onClick={() => setShowInfoModal(true)}
                  title="Ver ejemplos de configuración"
                  style={{ padding: '2px 8px', fontSize: '0.875rem' }}
                >
                  <i className="bi bi-info-circle me-1"></i>
                  Ver Ejemplos
                </Button>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={10}
                name="configuracion"
                value={JSON.stringify(formData.configuracion, null, 2)}
                onChange={(e) => {
                  const jsonValidation = validateJSON(e.target.value)
                  if (jsonValidation.valid) {
                    const structureValidation = validateConfigStructure(jsonValidation.config, formData.formato)
                    if (structureValidation.valid) {
                      setFormData(prev => ({ ...prev, configuracion: jsonValidation.config }))
                      // Limpiar error si existe
                      if (errors.configuracion) {
                        setErrors(prev => {
                          const newErrors = { ...prev }
                          delete newErrors.configuracion
                          return newErrors
                        })
                      }
                    } else {
                      setErrors(prev => ({
                        ...prev,
                        configuracion: structureValidation.error
                      }))
                    }
                  } else {
                    // Mantener el valor para que el usuario pueda seguir editando
                    // pero marcar el error
                    setErrors(prev => ({
                      ...prev,
                      configuracion: jsonValidation.error
                    }))
                  }
                }}
                isInvalid={!!errors.configuracion}
                placeholder={
                  formData.formato === 'CSV' 
                    ? '{"delimiter": ",", "encoding": "utf-8", "column_mapping": {"fecha": 0, "descripcion": 1, "monto": 2, "referencia": 3}}'
                    : formData.formato === 'XML'
                    ? '{"xpath_mapping": {"fecha": ".//fecha", "descripcion": ".//descripcion", "monto": ".//monto", "referencia": ".//referencia"}}'
                    : '{"configuracion_personalizada": "valor"}'
                }
                className={errors.configuracion ? 'is-invalid' : ''}
              />
              <Form.Control.Feedback type="invalid">
                {errors.configuracion}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                {formData.formato === 'CSV' && (
                  <>Configuración JSON para CSV. Campos requeridos: <code>column_mapping</code> (objeto con índices de columnas). Opcionales: <code>delimiter</code> (por defecto &quot;,&quot;), <code>encoding</code> (por defecto &quot;utf-8&quot;).</>
                )}
                {formData.formato === 'XML' && (
                  <>Configuración JSON para XML. Campo requerido: <code>xpath_mapping</code> (objeto con rutas XPath).</>
                )}
                {formData.formato === 'OFX' && (
                  <>OFX generalmente no requiere configuración personalizada. Puede dejar vacío o agregar opciones adicionales.</>
                )}
                {formData.formato === 'personalizado' && (
                  <>Configuración personalizada en formato JSON. Estructura libre según necesidades específicas.</>
                )}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="activo"
                label="Activo"
                checked={formData.activo}
                onChange={handleChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowForm(false); resetForm(); setSelectedFormat(null) }}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={saveMutation.isLoading}>
              {saveMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                selectedFormat ? 'Actualizar' : 'Crear'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal de información con ejemplos */}
      <Modal show={showInfoModal} onHide={() => setShowInfoModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-info-circle me-2"></i>
            Ejemplos de Configuración
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-4">
            <h6>Selecciona un formato para ver su ejemplo:</h6>
            <div className="d-flex gap-2 mb-3">
              <Button
                variant={formData.formato === 'CSV' ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, formato: 'CSV' }))}
              >
                CSV
              </Button>
              <Button
                variant={formData.formato === 'OFX' ? 'primary' : 'outline-info'}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, formato: 'OFX' }))}
              >
                OFX
              </Button>
              <Button
                variant={formData.formato === 'XML' ? 'primary' : 'outline-warning'}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, formato: 'XML' }))}
              >
                XML
              </Button>
              <Button
                variant={formData.formato === 'personalizado' ? 'primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, formato: 'personalizado' }))}
              >
                Personalizado
              </Button>
            </div>
          </div>

          {(() => {
            const examples = getConfigExamples()
            const currentExample = examples[formData.formato] || examples.CSV

            return (
              <div>
                <Alert variant="info" className="mb-3">
                  <strong>{currentExample.title}</strong>
                  <br />
                  {currentExample.description}
                </Alert>

                <div className="mb-3">
                  <h6>Ejemplo de Configuración JSON:</h6>
                  <pre
                    style={{
                      backgroundColor: '#f8f9fa',
                      padding: '15px',
                      borderRadius: '5px',
                      border: '1px solid #dee2e6',
                      fontSize: '0.9rem',
                      maxHeight: '300px',
                      overflow: 'auto'
                    }}
                  >
                    {JSON.stringify(currentExample.example, null, 2)}
                  </pre>
                </div>

                <div className="mb-3">
                  <h6>Explicación:</h6>
                  <p className="text-muted">{currentExample.explanation}</p>
                </div>

                <div className="d-flex gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        configuracion: currentExample.example
                      }))
                      setShowInfoModal(false)
                      toast.success('Ejemplo copiado a la configuración')
                    }}
                  >
                    <i className="bi bi-clipboard-check me-1"></i>
                    Usar este Ejemplo
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(JSON.stringify(currentExample.example, null, 2))
                      toast.success('Ejemplo copiado al portapapeles')
                    }}
                  >
                    <i className="bi bi-clipboard me-1"></i>
                    Copiar JSON
                  </Button>
                </div>
              </div>
            )
          })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowInfoModal(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default BankFormats
